import { useState, useCallback } from 'react';
import { profilesAPI, formatApiErrorDetail } from '../utils/api';
import { toast } from 'sonner';

export const useProfileManagement = () => {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Memoized to be stable for useEffect dependencies
  const loadProfiles = useCallback(async () => {
    try {
      const data = await profilesAPI.getAll();
      setProfiles(data);
    } catch (error) {
      toast.error('Errore nel caricamento dei profili');
    }
  }, []); // Empty deps: profilesAPI, setProfiles, toast are stable

  const createProfile = useCallback(async (profileData) => {
    setIsLoading(true);
    try {
      await profilesAPI.create(profileData);
      toast.success('Profilo creato con successo');
      await loadProfiles();
      return true;
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail);
      toast.error(errorMsg || 'Errore durante il salvataggio');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadProfiles]);

  const updateProfile = useCallback(async (id, profileData) => {
    setIsLoading(true);
    try {
      await profilesAPI.update(id, profileData);
      toast.success('Profilo aggiornato con successo');
      await loadProfiles();
      return true;
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail);
      toast.error(errorMsg || 'Errore durante il salvataggio');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadProfiles]);

  const deleteProfile = useCallback(async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo profilo?')) {
      try {
        await profilesAPI.delete(id);
        toast.success('Profilo eliminato');
        await loadProfiles();
        return true;
      } catch (error) {
        toast.error("Errore durante l'eliminazione");
        return false;
      }
    }
    return false;
  }, [loadProfiles]);

  const toggleActive = useCallback(async (profile) => {
    try {
      await profilesAPI.update(profile.id, {
        ...profile,
        attivo: !profile.attivo
      });
      toast.success('Stato profilo aggiornato');
      await loadProfiles();
      return true;
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
      return false;
    }
  }, [loadProfiles]);

  return {
    profiles,
    isLoading,
    loadProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    toggleActive
  };
};
